import React, { useState, useEffect } from 'react';
import { Menu, X, Globe, LogIn, ChevronDown } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isLanguageOpen, setIsLanguageOpen] = useState(false);
  const location = useLocation();
  const { language, setLanguage } = useLanguage();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId: string) => {
    if (location.pathname !== '/') {
      window.location.href = `/#${sectionId}`;
      return;
    }
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setIsMobileMenuOpen(false);
  };

  const navItems = {
    fr: [
      { name: 'Accueil', action: () => scrollToSection('hero') },
      { name: 'Expertise', link: '/expertise' },
      { name: 'Réalisations', link: '/case-studies' },
      { name: 'Blog', link: '/blog' },
      { name: 'Contact', action: () => scrollToSection('contact') }
    ],
    en: [
      { name: 'Home', action: () => scrollToSection('hero') },
      { name: 'Expertise', link: '/expertise' },
      { name: 'Case Studies', link: '/case-studies' },
      { name: 'Blog', link: '/blog' },
      { name: 'Contact', action: () => scrollToSection('contact') }
    ]
  };

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white/90 backdrop-blur-md shadow-sm' : 'bg-transparent'}`}>
      <nav className="container mx-auto px-6 py-4">
        <div className="flex items-center justify-between">
          <Link to="/" className="text-2xl font-bold text-navy-900">
            Julycia<span className="text-blue-600">.</span>
          </Link>
          
          <div className="hidden lg:flex items-center space-x-6">
            {navItems[language].map((item, index) => (
              <div key={item.name} className="relative group">
                {item.link ? (
                  <Link
                    to={item.link}
                    className="relative text-navy-700 hover:text-blue-600 transition-all duration-300 font-medium py-2 px-3 rounded-lg hover:bg-blue-50/50"
                    style={{ animationDelay: `${index * 100}ms` }}
                  >
                    {item.name}
                    <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-to-r from-blue-500 to-pink-500 group-hover:w-full transition-all duration-300"></span>
                  </Link>
                ) : (
                  <button
                    onClick={item.action}
                    className="relative text-navy-700 hover:text-blue-600 transition-all duration-300 font-medium py-2 px-3 rounded-lg hover:bg-blue-50/50"
                    style={{ animationDelay: `${index * 100}ms` }}
                  >
                    {item.name}
                    <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-to-r from-blue-500 to-pink-500 group-hover:w-full transition-all duration-300"></span>
                  </button>
                )}
              </div>
            ))}
            
            <div className="flex items-center space-x-3 ml-4">
              {/* Language Switcher compact */}
              <div className="relative">
                <button
                  onClick={() => setIsLanguageOpen(!isLanguageOpen)}
                  className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-gradient-to-r from-navy-50 to-blue-50 hover:from-navy-100 hover:to-blue-100 transition-all duration-300 border border-navy-200/50"
                >
                  <Globe size={14} className="text-navy-600" />
                  <span className="text-navy-700 text-xs font-medium">{language.toUpperCase()}</span>
                  <ChevronDown size={12} className={`text-navy-600 transition-transform duration-300 ${isLanguageOpen ? 'rotate-180' : ''}`} />
                </button>
                
                {isLanguageOpen && (
                  <div className="absolute top-full mt-2 right-0 bg-white rounded-lg shadow-xl border border-navy-200/50 overflow-hidden z-10 animate-fade-in min-w-28">
                    <button
                      onClick={() => { setLanguage('fr'); setIsLanguageOpen(false); }}
                      className="w-full px-3 py-2 text-left text-navy-700 hover:bg-blue-50 transition-colors duration-200 font-medium text-sm"
                    >
                      Français
                    </button>
                    <button
                      onClick={() => { setLanguage('en'); setIsLanguageOpen(false); }}
                      className="w-full px-3 py-2 text-left text-navy-700 hover:bg-blue-50 transition-colors duration-200 font-medium text-sm"
                    >
                      English
                    </button>
                  </div>
                )}
              </div>

              {/* Admin Login Link compact */}
              <Link 
                to="/admin/login"
                className="flex items-center gap-1.5 px-3 py-1.5 bg-gradient-to-r from-pink-500 to-blue-500 text-white rounded-lg hover:from-pink-600 hover:to-blue-600 transition-all duration-300 font-medium shadow-md hover:shadow-lg hover:scale-105 text-sm"
              >
                <LogIn size={14} />
                <span>{language === 'fr' ? 'Connexion' : 'Login'}</span>
              </Link>
            </div>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden text-navy-700 hover:text-blue-600 transition-colors p-2 rounded-lg hover:bg-blue-50/50"
          >
            {isMobileMenuOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden mt-4 pb-4 border-t border-navy-200 pt-4 animate-fade-in">
            <div className="space-y-1">
              {navItems[language].map((item, index) => (
                <div key={item.name} style={{ animationDelay: `${index * 100}ms` }} className="animate-fade-in">
                  {item.link ? (
                    <Link
                      to={item.link}
                      onClick={() => setIsMobileMenuOpen(false)}
                      className="block py-2.5 px-3 text-navy-700 hover:text-blue-600 transition-all duration-300 font-medium rounded-lg hover:bg-blue-50"
                    >
                      {item.name}
                    </Link>
                  ) : (
                    <button
                      onClick={item.action}
                      className="block w-full text-left py-2.5 px-3 text-navy-700 hover:text-blue-600 transition-all duration-300 font-medium rounded-lg hover:bg-blue-50"
                    >
                      {item.name}
                    </button>
                  )}
                </div>
              ))}
              
              {/* Mobile Admin Login Link */}
              <Link 
                to="/admin/login"
                onClick={() => setIsMobileMenuOpen(false)}
                className="flex items-center gap-2 py-2.5 px-3 bg-gradient-to-r from-pink-500 to-blue-500 text-white rounded-lg font-medium mt-3"
              >
                <LogIn size={16} />
                <span>{language === 'fr' ? 'Connexion' : 'Login'}</span>
              </Link>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
};

export default Header;
